import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, Divider, CircularProgress } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import DashboardLayout from 'utils/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'utils/Navbars/DashboardNavbar';
import Footer from 'utils/Footer';
import FriendInfoCard from 'utils/Cards/InfoCards/FriendInfoCard';
import VuiInput from "components/VuiInput";
import FriendRequestButton from 'components/CustomButtons/FriendRequestButton';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { ReactComponent as NoPendingFriends } from "assets/svgs/noPendingFriends.svg";
import { ReactComponent as NotFound } from "assets/svgs/friends.svg";
import dataService from 'api/data.service';

const MyPeople = () => {
  const { t } = useTranslation();
  const [friends, setFriends] = useState([]);
  const [allFriends, setAllFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchDebounce = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [friendsData, requestsData] = await Promise.all([
          dataService.getFriends(1, 10, ''),
          dataService.getPendingFriendRequests(),
        ]);
        setFriends(friendsData.resources);
        setAllFriends(friendsData.resources);
        setPendingRequests(requestsData.resources);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchTerm(value);
    clearTimeout(searchDebounce.current);

    if (value.length > 2) {
      searchDebounce.current = setTimeout(async () => {
        setSearchLoading(true);
        try {
          const results = await dataService.getFriends(1, 5, value);
          setFriends(results.resources);
        } catch (error) {
          console.error('Error fetching search results:', error);
          setFriends([]);
        } finally {
          setSearchLoading(false);
        }
      }, 350);
    } else {
      setFriends(allFriends);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} width="100%" sx={{ minWidth: '300px' }}>
            <VuiBox display="flex" mb={2} width="100%" alignItems="center" sx={{ gap: 1 }}>
              <VuiInput
                placeholder={t('searchFriends')}
                icon={{ component: 'search', direction: 'left' }}
                value={searchTerm}
                onChange={handleSearchChange}
                sx={({ breakpoints }) => ({
                  backgroundColor: 'info.main !important',
                  flex: 1,
                  minWidth: '300px',
                })}
              />
              {searchLoading && <CircularProgress size={20} sx={{ color: "#0075ff", flexShrink: 0 }} />}
            </VuiBox>
          </Grid>

          <Grid item xs={12} md={8} sx={{ width: '100%' }}>
            {loading ? (
              <VuiBox display="flex" justifyContent="center" py={6}>
                <CircularProgress size={32} sx={{ color: "#0075ff" }} />
              </VuiBox>
            ) : (
              <Grid container spacing={3}>
                {friends.length > 0 ? (
                  friends.map((friend) => (
                    <Grid item key={friend.id} xs={12} md={6}>
                      <FriendInfoCard friend={friend} />
                    </Grid>
                  ))
                ) : (
                  <VuiBox mt={2} textAlign="center" width="100%">
                    <NotFound style={{ width: '55%', height: '55%' }} />
                    <Typography variant="h5" sx={{ color: 'white !important' }}>{t('notFriends')}</Typography>
                    <Typography variant="h5" sx={{ color: 'white !important' }}>{t('connectPeople')}</Typography>
                  </VuiBox>
                )}
              </Grid>
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <VuiBox
              sx={{
                background: "linear-gradient(126.97deg, rgba(6,11,40,0.92) 28.26%, rgba(10,14,35,0.85) 91.2%)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "16px",
                padding: "20px",
              }}
            >
              <VuiBox display="flex" alignItems="center" mb={2} sx={{ gap: 1 }}>
                <PeopleIcon sx={{ color: "#0075ff", fontSize: "1.3rem" }} />
                <VuiTypography variant="h6" color="white" fontWeight="bold">
                  {t('pendingFriendRequests')}
                </VuiTypography>
              </VuiBox>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", mb: 2 }} />
              {pendingRequests.length > 0 ? (
                pendingRequests.map((request, i) => (
                  <VuiBox key={request.id} mb={1}>
                    <FriendRequestButton
                      userId={request.friend_id}
                      currentUserId={request.user_id}
                      name={request.name}
                      showViewButton="true"
                      avatar={request.avatar}
                    />
                    {i < pendingRequests.length - 1 && (
                      <Divider sx={{ borderColor: "rgba(255,255,255,0.06)", mt: 1 }} />
                    )}
                  </VuiBox>
                ))
              ) : (
                <VuiBox mt={2} textAlign="center">
                  <NoPendingFriends style={{ width: '55%', height: '55%' }} />
                  <Typography variant="h6" sx={{ color: '#a0aec0 !important', mt: 1 }}>
                    {t('noPendingRequests')}
                  </Typography>
                </VuiBox>
              )}
            </VuiBox>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
};

export default MyPeople;
